import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { getLocalItem, setLocalItem, removeLocalItem, getUserIdFromToken } from '../utils/storage'
import { fetchConversations, deleteConversation } from '../utils/chatApi'

// Return a stable key for the current user (used to guard async callbacks)
const getCurrentUserId = () => getUserIdFromToken()

const ChatHistoryContext = createContext()

export const useChatHistory = () => {
    const context = useContext(ChatHistoryContext)
    if (!context) {
        throw new Error('useChatHistory must be used within a ChatHistoryProvider')
    }
    return context
}

const STORAGE_KEY = 'chatHistory_v1'
const ACTIVE_CHAT_KEY = 'activeChatId_v1'

const generateChatId = () => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

const generateChatTitle = (text) => {
    if (!text) return 'New Conversation'
    const cleanText = text.trim().substring(0, 40)
    return cleanText.length < text.trim().length ? `${cleanText}...` : cleanText
}

// Function to load conversations from API
const loadConversationsFromApi = async () => {
    try {
        const response = await fetchConversations()
        // Extract conversations array from response
        const conversationsArray = response.conversations || []
        
        // Transform API response to match our local structure
        return conversationsArray.map(conv => ({
            id: conv.conversation_id || `conv_${conv.conversation_id}`,
            title: conv.messages && conv.messages.length > 0 
                ? generateChatTitle(conv.messages[conv.messages.length - 1].query) 
                : 'New Conversation',
            messages: conv.messages ? conv.messages.flatMap(msg => [
                // User message
                {
                    from: 'You',
                    text: msg.query || '',
                    timestamp: msg.timestamp
                },
                // Bot response
                {
                    from: 'bot', 
                    text: msg.response || '',
                    timestamp: msg.timestamp
                }
            ]).filter(msg => msg.text) : [],
            conversationId: conv.conversation_id,
            createdAt: conv.messages && conv.messages.length > 0 
                ? new Date((conv.messages[0].timestamp || 0) * 1000).toISOString()
                : new Date().toISOString(),
            updatedAt: conv.messages && conv.messages.length > 0 
                ? new Date((conv.messages[conv.messages.length - 1].timestamp || 0) * 1000).toISOString()
                : new Date().toISOString()
        }))
    } catch (error) {
        console.error('Failed to load conversations from API:', error)
        throw error
    }
}

export const ChatHistoryProvider = ({ children }) => {
    const previousUserIdRef = useRef(null)
    const isInitializedRef = useRef(false)

    const [conversations, setConversations] = useState([])
    const [activeChatId, setActiveChatIdState] = useState(null)
    const [isLoadingConversations, setIsLoadingConversations] = useState(false)

    // Load chat history for current user and detect user changes
    useEffect(() => {
        const loadChatHistoryForUser = async () => {
            const currentUserId = getUserIdFromToken()

            // If this is the first initialization, load the current user's chats
            if (!isInitializedRef.current) {
                isInitializedRef.current = true
                previousUserIdRef.current = currentUserId

                // Try to load from API first, fallback to localStorage
                try {
                    setIsLoadingConversations(true)
                    const apiConversations = await loadConversationsFromApi()
                    // Guard: skip if user changed while we were waiting
                    if (getUserIdFromToken() === currentUserId) {
                        setConversations(apiConversations)
                    }
                } catch (error) {
                    console.warn('Failed to load from API, falling back to localStorage:', error)
                    // Fallback to localStorage (already namespaced per user via storage.js)
                    try {
                        const saved = getLocalItem(STORAGE_KEY)
                        if (saved && Array.isArray(saved) && getUserIdFromToken() === currentUserId) {
                            setConversations(saved)
                        }
                    } catch (e) {
                        console.error('Failed to load chat history from localStorage', e)
                    }
                } finally {
                    setIsLoadingConversations(false)
                }

                try {
                    const savedActiveChatId = getLocalItem(ACTIVE_CHAT_KEY)
                    if (savedActiveChatId) {
                        setActiveChatIdState(savedActiveChatId)
                    }
                } catch (e) {
                    // Ignore
                }
                return
            }

            // Check if user changed
            if (previousUserIdRef.current !== currentUserId) {
                // Always clear existing state before loading the new user's history —
                // this prevents a flash of the previous user's conversations.
                setConversations([])
                setActiveChatIdState(null)

                // Load new user's chat history
                previousUserIdRef.current = currentUserId

                // Try to load from API first, fallback to localStorage
                try {
                    setIsLoadingConversations(true)
                    const apiConversations = await loadConversationsFromApi()
                    // Guard: skip if user changed again while we were waiting
                    if (getUserIdFromToken() === currentUserId) {
                        setConversations(apiConversations)
                    }
                } catch (error) {
                    console.warn('Failed to load from API, falling back to localStorage:', error)
                    // Fallback to localStorage (already namespaced per user via storage.js)
                    try {
                        const saved = getLocalItem(STORAGE_KEY)
                        if (saved && Array.isArray(saved) && getUserIdFromToken() === currentUserId) {
                            setConversations(saved)
                        } else {
                            setConversations([])
                        }
                    } catch (e) {
                        console.error('Failed to load chat history from localStorage', e)
                        setConversations([])
                    }
                } finally {
                    setIsLoadingConversations(false)
                }

                try {
                    const savedActiveChatId = getLocalItem(ACTIVE_CHAT_KEY)
                    setActiveChatIdState(savedActiveChatId || null)
                } catch (e) {
                    setActiveChatIdState(null)
                }
            }
        }

        // Load on mount
        loadChatHistoryForUser()

        // Listen for storage changes (e.g., when authToken is set/removed in other tabs)
        const handleStorageChange = (e) => {
            if (e.key === 'authToken' || e.key === null) {
                // authToken changed or storage was cleared
                loadChatHistoryForUser()
            }
        }
        window.addEventListener('storage', handleStorageChange)

        // Check periodically for user changes (e.g., when user logs in/out in same window)
        const interval = setInterval(loadChatHistoryForUser, 1000)

        return () => {
            window.removeEventListener('storage', handleStorageChange)
            clearInterval(interval)
        }
    }, [])

    // Persist activeChatId
    const setActiveChatId = useCallback((id) => {
        setActiveChatIdState(id)
        if (id) {
            setLocalItem(ACTIVE_CHAT_KEY, id)
        } else {
            removeLocalItem(ACTIVE_CHAT_KEY)
        }
    }, [])

    // Persist conversations
    useEffect(() => {
        try {
            setLocalItem(STORAGE_KEY, conversations)
        } catch (e) {
            console.error('Failed to save chat history', e)
        }
    }, [conversations])

    const activeConversation = useMemo(() => {
        return conversations.find(c => c.id === activeChatId) || null
    }, [conversations, activeChatId])

    const createNewChat = useCallback((initialText = null, initialMessages = []) => {
        const newChatId = generateChatId()
        const title = initialText
            ? generateChatTitle(initialText)
            : 'New Conversation'

        const newConversation = {
            id: newChatId,
            title,
            messages: initialMessages,
            conversationId: null, // Store conversation_id per chat
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        }

        setConversations(prev => [newConversation, ...prev])
        setActiveChatId(newChatId)
        return newChatId
    }, [setActiveChatId])

    const updateMessages = useCallback((chatId, messagesOrFn) => {
        setConversations(prev => {
            const updated = prev.map(c => {
                if (c.id === chatId) {
                    const newMessages = typeof messagesOrFn === 'function'
                        ? messagesOrFn(c.messages)
                        : messagesOrFn

                    // Auto-generate title if it's the first message
                    let newTitle = c.title
                    if (c.title === 'New Conversation' && newMessages.length > 0) {
                        const firstUserMsg = newMessages.find(m => m.from === 'You')
                        if (firstUserMsg) {
                            newTitle = generateChatTitle(firstUserMsg.text)
                        }
                    }

                    return {
                        ...c,
                        title: newTitle,
                        messages: newMessages,
                        updatedAt: new Date().toISOString()
                    }
                }
                return c
            })

            // Sort by updatedAt (most recent first)
            return [...updated].sort((a, b) =>
                new Date(b.updatedAt) - new Date(a.updatedAt)
            )
        })
    }, [])

    const addMessage = useCallback((message, chatId = activeChatId) => {
        if (!chatId) {
            // If no active chat, create one with the message
            return createNewChat(message.from === 'You' ? message.text : null, [message])
        } else {
            updateMessages(chatId, (prev) => [...prev, message])
            return chatId
        }
    }, [activeChatId, createNewChat, updateMessages])

    const switchChat = useCallback((chatId) => {
        setActiveChatId(chatId)
    }, [setActiveChatId])

    const deleteChat = useCallback(async (chatId) => {
        const conversation = conversations.find(c => c.id === chatId)
        
        // Try to delete from API if conversation has a conversationId
        if (conversation?.conversationId) {
            try {
                await deleteConversation(conversation.conversationId)
            } catch (error) {
                console.warn('Failed to delete conversation from API, removing locally only:', error)
                // Continue with local deletion even if API fails
            }
        }
        
        // Always remove from local state
        setConversations(prev => prev.filter(c => c.id !== chatId))
        if (activeChatId === chatId) {
            setActiveChatId(null)
        }
    }, [conversations, activeChatId, setActiveChatId])

    const renameChat = useCallback((chatId, newTitle) => {
        setConversations(prev => prev.map(c =>
            c.id === chatId ? { ...c, title: newTitle } : c
        ))
    }, [])

    const clearAllHistory = useCallback(() => {
        setConversations([])
        setActiveChatId(null)
        // Also clear from storage
        try {
            removeLocalItem(STORAGE_KEY)
            removeLocalItem(ACTIVE_CHAT_KEY)
        } catch (e) {
            // Ignore errors
        }
    }, [setActiveChatId])

    const updateConversationId = useCallback((chatId, conversationId) => {
        setConversations(prev => {
            return prev.map(c => {
                if (c.id === chatId) {
                    return {
                        ...c,
                        conversationId: conversationId,
                        updatedAt: new Date().toISOString()
                    }
                }
                return c
            })
        })
    }, [])

    // Function to refresh conversations from API
    const refreshConversations = useCallback(async () => {
        const userIdAtStart = getCurrentUserId()
        try {
            setIsLoadingConversations(true)
            const apiConversations = await loadConversationsFromApi()
            setConversations(prev => {
                // Bail out if the user changed while we were waiting for the API
                if (getCurrentUserId() !== userIdAtStart) return prev

                // Preserve only locally-created conversations that haven't been
                // assigned a conversationId yet (i.e. not yet known to the API).
                // Conversations that DO have a conversationId but are missing from
                // the API response belong to a different user or were deleted.
                const localOnly = prev.filter(c => !c.conversationId)
                const merged = [...apiConversations]
                localOnly.forEach(localConv => {
                    if (!merged.find(c => c.id === localConv.id)) {
                        merged.push(localConv)
                    }
                })

                merged.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt))
                return merged
            })
        } catch (error) {
            console.warn('Failed to refresh conversations from API:', error)
        } finally {
            setIsLoadingConversations(false)
        }
    }, [])

    const value = {
        conversations,
        activeChatId,
        activeConversation,
        createNewChat,
        updateMessages,
        addMessage,
        switchChat,
        deleteChat,
        renameChat,
        clearAllHistory,
        updateConversationId,
        refreshConversations,
        isLoadingConversations
    }

    return (
        <ChatHistoryContext.Provider value={value}>
            {children}
        </ChatHistoryContext.Provider>
    )
}
